import { format, isValid, parseISO } from "date-fns"

/** Group default window passed into Add Volunteer (date part only; may include time in ISO string). */
export type DefaultServePeriod = {
    from: string
    to: string
    regionId?: number
}

/** Calendar day from API `YYYY-MM-DD` or ISO prefix — same as create form defaults (local calendar, no UTC midnight shift). */
export function localDateFromApiDateString(value: string): Date {
    return parseISO(value.trim().slice(0, 10))
}

/** Serve boundary from API (ISO or `YYYY-MM-DD`) or `Date` → `Date` for range inputs (calendar-local, not full-ISO `new Date` shift). */
export function parseServePeriodBoundary(value: unknown): Date | undefined {
    if (value == null || value === "") return undefined
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? undefined : value
    }
    if (typeof value === "string") {
        const t = value.trim()
        if (!t) return undefined
        const d = localDateFromApiDateString(t)
        return isValid(d) ? d : undefined
    }
    return undefined
}

export type ServePeriodFormValue = {
    from: Date
    to: Date
    regionId?: number
}

export type ServePeriodLike = {
    from?: unknown
    to?: unknown
    regionId?: unknown
}

type ApiServeDate = { from?: string; to?: string; regionId?: number }

function parseRegionId(value: unknown): number | undefined {
    if (value == null || String(value).trim() === "") return undefined
    const n = typeof value === "number" ? value : parseInt(String(value), 10)
    return Number.isNaN(n) ? undefined : n
}

function toFormPeriod(period: ServePeriodLike): ServePeriodFormValue | null {
    const from = parseServePeriodBoundary(period.from)
    const to = parseServePeriodBoundary(period.to)
    if (!from || !to) return null
    return { from, to, regionId: parseRegionId(period.regionId) }
}

function calendarRangeKey(from: Date, to: Date): string {
    return `${format(from, "yyyy-MM-dd")}|${format(to, "yyyy-MM-dd")}`
}

/**
 * Union of existing serve periods plus incoming ones.
 * Dedupes by calendar from/to so adding a group window cannot drop historical dates.
 */
export function mergeServePeriodLists(
    existing: ServePeriodLike[] | null | undefined,
    incoming: ServePeriodLike[] | null | undefined,
): ServePeriodFormValue[] {
    const periods: ServePeriodFormValue[] = []
    const seen = new Set<string>()
    for (const raw of [...(existing ?? []), ...(incoming ?? [])]) {
        const period = toFormPeriod(raw)
        if (!period) continue
        const key = calendarRangeKey(period.from, period.to)
        if (seen.has(key)) continue
        seen.add(key)
        periods.push(period)
    }
    return periods
}

/**
 * Normalize API serve dates to Date (same representation as makeDefaultEntry).
 * If defaultServePeriod is set, append it when no existing period matches the same calendar range.
 */
export function mergeServePeriodsForGroupAddForm(
    serveDates: ApiServeDate[] | undefined | null,
    defaultServePeriod?: DefaultServePeriod,
): ServePeriodFormValue[] {
    return mergeServePeriodLists(
        serveDates,
        defaultServePeriod ? [defaultServePeriod] : [],
    )
}
